import styles from './ClientTiktokAudios.module.css';
import { useMemo } from 'react';
import useUrlState from '../../../../../../Hooks/useUrlState';
import ClientTiktokAudiosTable, {
    ClientTiktokAudiosTableOrderingValues,
} from './ClientTiktokAudiosTable/ClientTiktokAudiosTable';
import { useClientTiktokAudios } from './useClientTiktokAudios';
import { ClientExploreTiktokAudiosTableRow } from '../../../../TikTok.types';
import { DebounceInput } from 'react-debounce-input';
import SearchInput from '../../../../../../ui/DataEntry/SearchInput/SearchInput';
import Select from '../../../../../../ui/DataEntry/Select/Select';
import { GenericDropdownOption } from '../../../../../../App.types';
import { StylesConfig, ValueType } from 'react-select';
import cn from 'classnames';
import ClientTiktokAudioModal from '../../details/ClientTiktokAudioModal/ClientTiktokAudioModal';

type ClientTiktokAudiosProps = {
    clientId: number;
};

type UrlState = Partial<{
    page: number;
    pageSize: number;
    search: string;
    orderByDesc?: ClientTiktokAudiosTableOrderingValues;
    minVideoCreations?: number;
    maxVideoCreations?: number;
    soundType?: boolean;
    audioModal?: number;
}>;

const sortingOptions: GenericDropdownOption<ClientTiktokAudiosTableOrderingValues>[] = [
    { label: 'Total Videos', value: '-video_count' },
    { label: 'Daily Growth (# videos)', value: '-video_count_daily_change' },
    { label: 'Daily Growth (% uplift)', value: '-video_count_daily_change_relative' },
    { label: 'Weekly Growth (# videos)', value: '-video_count_weekly_change' },
    { label: 'Weekly Growth (% uplift)', value: '-video_count_weekly_change_relative' },
];

const sortingSelectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        borderColor: '#C2CFE0',
    }),
    valueContainer: (base) => ({
        ...base,
        minWidth: '10rem',
    }),
    singleValue: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
    menuList: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
};

const soundTypeOptions: GenericDropdownOption<boolean | undefined>[] = [
    { label: 'All', value: undefined },
    { label: 'Official', value: false },
    { label: 'Original', value: true },
];

const soundTypeSelectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        border: '1px solid #c2cfe0',
        borderRadius: '0.375rem',
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '0.5rem',
        fontWeight: 'normal',
        minWidth: '5.5rem',
        fontSize: '0.85rem',
    }),
    menu: (base) => ({
        ...base,
        fontSize: '0.85rem',
    }),
    dropdownIndicator: (base) => ({
        ...base,
        padding: '0.2rem',
        paddingLeft: 0,
    }),
};

const ClientTiktokAudios = ({ clientId }: ClientTiktokAudiosProps) => {
    const [urlState, setUrlState] = useUrlState<UrlState>({
        page: 1,
        pageSize: 25,
        search: '',
        orderByDesc: '-video_count_daily_change',
        minVideoCreations: 100,
    });

    const pageSize = Number(urlState.pageSize);
    const setPageSize = (pageSize: number | undefined) => {
        setUrlState({ pageSize });
    };

    const page = Number(urlState.page);
    const setPage = (page: number | undefined) => {
        setUrlState({ page });
    };

    const search = urlState.search ?? '';
    const setSearch = (search: string) => setUrlState({ search, page: 1 });

    const orderByDesc = urlState.orderByDesc;
    const setOrderByDesc = (orderByDesc: ClientTiktokAudiosTableOrderingValues | undefined) =>
        setUrlState({ orderByDesc });

    const minVideoCreations = Number(urlState.minVideoCreations) || undefined;
    const setMinVideoCreations = (e: React.ChangeEvent<HTMLInputElement>) =>
        setUrlState({ minVideoCreations: Number(e.target.value) || undefined, page: 1 });

    const maxVideoCreations = Number(urlState.maxVideoCreations) || undefined;
    const setMaxVideoCreations = (e: React.ChangeEvent<HTMLInputElement>) =>
        setUrlState({ maxVideoCreations: Number(e.target.value) || undefined, page: 1 });

    const soundType = soundTypeOptions.find((type) => String(type.value) === String(urlState.soundType));
    const setSoundType = (option: ValueType<GenericDropdownOption<boolean | undefined>, false>) =>
        setUrlState({ soundType: option?.value, page: 1 });

    const audioIdForModal = Number(urlState.audioModal);
    const setAudioIdForModal = (id: number) => setUrlState({ audioModal: id });

    const {
        tiktokAudioStats,
        statsCount,
        tiktokAudioAssets,
        linkedSongs,
        isLoading,
        hasError: hasErrorLoadingStats,
        isInitialized,
    } = useClientTiktokAudios({
        clientId,
        page: Number(urlState.page),
        page_size: Number(urlState.pageSize),
        search: urlState.search,
        ordering: urlState.orderByDesc as ClientTiktokAudiosTableOrderingValues,
        min_video_count: minVideoCreations,
        max_video_count: maxVideoCreations,
        is_original: soundType?.value,
    });

    const rows: ClientExploreTiktokAudiosTableRow[] = useMemo(
        () =>
            tiktokAudioStats.map((stats) => {
                const asset = tiktokAudioAssets.find((asset) => asset.audio_id === stats.audio_id);
                return {
                    ...stats,
                    image_url: asset?.image_thumb.cached_url || asset?.image_thumb.original_url,
                    audioUrl: asset?.audio_file.cached_url || asset?.audio_file.original_url,
                    dailyGrowth: stats.standardised_data.daily_video_counts.reduce(
                        (acc, current) => {
                            acc.push({
                                timestamp: current.timestamp,
                                value: current.change > 0 ? current.change : 0,
                            });

                            return acc;
                        },
                        [] as ClientExploreTiktokAudiosTableRow['dailyGrowth']
                    ),
                    linkedSongs: linkedSongs.filter((s) => s.tiktok_audios.includes(stats.audio_id)),
                };
            }),
        [tiktokAudioStats, tiktokAudioAssets, linkedSongs]
    );

    const selectedTiktokAudioStats = useMemo(
        () => rows.find((row) => row.audio_id === audioIdForModal) ?? null,
        [audioIdForModal, rows]
    );

    const noDataLabel = hasErrorLoadingStats ? 'Sorry, we are experiencing technical issues' : 'No audios found';
    const selectedSortingValue = sortingOptions.find((option) => option.value === orderByDesc);

    return (
        <>
            <div className={styles.container}>
                <p className={styles.title}>TikTok Audio Search</p>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <SearchInput className={styles.searchInput} value={search} onChange={setSearch} />
                        <div className={styles.filterField}>
                            <label className={styles.filterLabel} htmlFor="minVideoCreations">
                                Min video creations
                            </label>
                            <DebounceInput
                                id="minVideoCreations"
                                type="number"
                                className={styles.filterInput}
                                debounceTimeout={500}
                                value={minVideoCreations}
                                onChange={setMinVideoCreations}
                            />
                        </div>

                        <div className={styles.filterField}>
                            <label className={styles.filterLabel} htmlFor="maxVideoCreations">
                                Max video creations
                            </label>
                            <DebounceInput
                                placeholder="Unlimited"
                                id="maxVideoCreations"
                                type="number"
                                className={styles.filterInput}
                                debounceTimeout={500}
                                value={maxVideoCreations}
                                onChange={setMaxVideoCreations}
                            />
                        </div>

                        <div className={cn(styles.filterField, styles.soundTypeField)}>
                            <label className={styles.filterLabel} htmlFor="audioType">
                                Audio type
                            </label>
                            <Select
                                id="audioType"
                                className={styles.soundTypeSelect}
                                isSearchable={false}
                                styles={soundTypeSelectStyles}
                                value={soundType}
                                options={soundTypeOptions}
                                onChange={setSoundType}
                                components={{
                                    IndicatorSeparator: null,
                                }}
                            />
                        </div>
                    </div>

                    <div className={styles.headerRight}>
                        <label className={styles.label}>Sort by</label>
                        <div data-test-id="sort-container">
                            <Select
                                isSearchable={false}
                                styles={sortingSelectStyles}
                                value={selectedSortingValue}
                                options={sortingOptions}
                                onChange={(option) => setOrderByDesc(option?.value)}
                            />
                        </div>
                    </div>
                </div>
                <ClientTiktokAudiosTable
                    data={rows}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    setPage={setPage}
                    page={page}
                    loading={isLoading || (!isInitialized && !hasErrorLoadingStats)}
                    noDataLabel={noDataLabel}
                    count={statsCount}
                    orderByDesc={orderByDesc}
                    onRowClick={(row) => setAudioIdForModal(row.audio_id)}
                />
            </div>
            <ClientTiktokAudioModal
                isModalOpen={!isNaN(audioIdForModal)}
                audioStatsTableRow={selectedTiktokAudioStats}
                clientId={clientId}
                closeModal={() => setUrlState({ audioModal: undefined })}
                audioId={audioIdForModal}
            />
        </>
    );
};

export default ClientTiktokAudios;
